import React, { useState, useEffect, useRef } from "react";
import { Search, Plus, ChevronRight, ChevronDown } from "lucide-react";
import DeleteIcon from "@mui/icons-material/Delete";
import AddMemberModal from "./AddResearcherModal";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";

const ROLES = ["Admin", "Researcher"];

const Researchers = () => {
  const navigate = useNavigate();
  const { userData, refreshUser } = useUser();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [researchers, setResearchers] = useState([]);
  const [profilePics, setProfilePics] = useState({});
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [togglingStatusId, setTogglingStatusId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const dropdownRefs = useRef({});
  const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
  const PROFILE_PIC_BUCKET =
    ENVIRONMENT === "production"
      ? "generativeinsight-user-survey"
      : "generativeinsight-user-survey-dev";

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openDropdownId !== null) {
        const ref = dropdownRefs.current[openDropdownId];
        if (ref && !ref.contains(e.target)) {
          setOpenDropdownId(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdownId]);

  const emailToName = (email) => {
    if (!email) return "User";
    const base = email.split("@")[0].replace(/[^\w\s]/g, " ");
    return base
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  const getInitials = (name) => {
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const formatRole = (role) => {
    if (!role) return "Researcher";
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  };

  const formatStatus = (status) => {
    if (!status) return "Pending";
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const getRoleColor = (role) => {
    const r = formatRole(role);
    if (r === "Admin") return "#fbbf24";
    if (r === "Collaborator") return "#a78bfa";
    return "#10b981";
  };
  const fetchProfilePicture = async (profilePicKey) => {
    if (!profilePicKey) return null;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/getS3Url`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bucket: PROFILE_PIC_BUCKET,
            key: profilePicKey,
          }),
        },
      );
      const data = await res.json();
      if (data.success && data.preSignedUrl) return data.preSignedUrl;
    } catch (err) {
      console.error("Error fetching profile picture:", err);
    }
    return null;
  };

  useEffect(() => {
    const fetchResearchers = async () => {
      const token = Cookies.get("token");
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/user/getAllUsers`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json", token },
          },
        );
        const data = await res.json();
        if (data?.success && Array.isArray(data.data)) {
          const mapped = data.data.map((u, index) => {
            const name =
              u.first_name && u.first_name.trim().length > 0
                ? u.first_name
                : emailToName(u.email);
            return {
              id: u._id || index + 1,
              name,
              email: u.email,
              initials: getInitials(name),
              role: u.role ? u.role : "Researcher",
              isActive: u.isActive === true,
              status: u.isActive === true ? "Active" : "Deactivated",
              deactivationStatus: u.deactivationStatus || false,
              project_count: u.project_count ?? 0,
              profile_pic: u.profile_pic || null,
            };
          });
          setResearchers(mapped);

          const pics = await Promise.all(
            mapped
              .filter((r) => r.profile_pic)
              .map(async (r) => ({
                id: r.id,
                url: await fetchProfilePicture(r.profile_pic),
              })),
          );
          const picsMap = {};
          pics.forEach((p) => {
            if (p.url) picsMap[p.id] = p.url;
          });
          setProfilePics(picsMap);
        }
      } catch (err) {
        console.error("Failed to load members:", err);
      }
    };
    fetchResearchers();
  }, []);

  const handleRoleChange = async (researcher, newRole) => {
    setOpenDropdownId(null);
    if (formatRole(researcher.role) === newRole) return;

    setUpdatingRoleId(researcher.id);
    const token = Cookies.get("token");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user/updateRoles`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", token },
          body: JSON.stringify({ userId: researcher.id, role: newRole }),
        },
      );
      const data = await res.json();
      if (data.success) {
        setResearchers((prev) =>
          prev.map((r) =>
            r.id === researcher.id ? { ...r, role: newRole } : r,
          ),
        );
        if (
          userData?.user_id === researcher.id ||
          userData?._id === researcher.id
        ) {
          await refreshUser();
          navigate("/");
        }
      } else {
        console.error("Failed to update role:", data.message);
      }
    } catch (err) {
      console.error("Error updating role:", err);
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleToggleStatus = async (researcher, e) => {
    e.stopPropagation();
    const shouldReactivate = researcher.deactivationStatus === true;
    const endpoint = shouldReactivate
      ? "/user/reactivateUser"
      : "/user/deactivateUser";
    const actionText = shouldReactivate ? "reactivate" : "deactivate";

    if (
      !window.confirm(
        `Are you sure you want to ${actionText} ${researcher.name}?`,
      )
    ) {
      return;
    }

    setTogglingStatusId(researcher.id);
    const token = Cookies.get("token");

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", token },
        body: JSON.stringify({
          userId: researcher.id,
          email: researcher.email,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setResearchers((prev) =>
          prev.map((r) => {
            if (r.id === researcher.id) {
              if (shouldReactivate) {
                return { ...r, status: "Active", deactivationStatus: false };
              } else {
                return { ...r, deactivationStatus: true };
              }
            }
            return r;
          }),
        );
      } else {
        alert(data.message || `Failed to ${actionText} user`);
      }
    } catch (err) {
      console.error(`Error ${actionText}ing user:`, err);
      alert(`Failed to ${actionText} user`);
    } finally {
      setTogglingStatusId(null);
    }
  };

  const handleDeleteUser = async (researcher, e) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `Are you sure you want to permanently remove ${researcher.name}? This action cannot be undone.`,
      )
    ) {
      return;
    }

    setDeletingId(researcher.id);
    const token = Cookies.get("token");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user/removeInvite`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", token },
          body: JSON.stringify({
            userId: researcher.id,
          }),
        },
      );
      const data = await res.json();
      if (data.success) {
        setResearchers((prev) => prev.filter((r) => r.id !== researcher.id));
      } else {
        alert(data.message || "Failed to remove user");
      }
    } catch (err) {
      console.error("Error removing user:", err);
      alert("Failed to remove user");
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddMember = (newMember) => {
    setResearchers([...researchers, newMember]);
  };

  const filteredResearchers = researchers.filter((r) => {
    const matchesSearch =
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.email.toLowerCase().includes(searchQuery.toLowerCase());

    const isPending = r.isActive === false && r.deactivationStatus === false;

    const isDeactivated = r.deactivationStatus === true;

    const isActive = r.isActive === true && r.deactivationStatus === false;

    const matchesFilter =
      activeFilter === "all" ||
      (activeFilter === "pending" && isPending) ||
      (activeFilter === "active" && isActive) ||
      (activeFilter === "deactivated" && isDeactivated);

    return matchesSearch && matchesFilter;
  });

  const totalMembers = researchers.length;

  const activeMembers = researchers.filter(
    (r) => r.isActive === true && r.deactivationStatus === false,
  ).length;

  const deactivatedMembers = researchers.filter(
    (r) => r.deactivationStatus === true,
  ).length;

  const pendingMembers = researchers.filter(
    (r) => r.isActive === false && r.deactivationStatus === false,
  ).length;
  return (
    <div className="admin-container">
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <div className="members-header">
          <div className="admin-search-bar">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="members-filter-tabs">
            {["all", "pending", "active", "deactivated"].map((f) => (
              <button
                key={f}
                className={`members-filter-tab ${activeFilter === f ? "active" : ""}`}
                onClick={() => setActiveFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <button
            className="admin-btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={18} />
            Add Member
          </button>
        </div>

        <div className="members-stats-row">
          <div className="members-stat-card">
            <span className="members-stat-label">Total Members</span>
            <span className="members-stat-value members-stat-accent">
              {totalMembers}
            </span>
          </div>
          <div className="members-stat-card">
            <span className="members-stat-label">Pending</span>
            <span className="members-stat-value members-stat-yellow">
              {pendingMembers}
            </span>
          </div>
          <div className="members-stat-card">
            <span className="members-stat-label">Active</span>
            <span className="members-stat-value members-stat-green">
              {activeMembers}
            </span>
          </div>
          <div className="members-stat-card">
            <span className="members-stat-label">Deactivated</span>
            <span className="members-stat-value members-stat-red">
              {deactivatedMembers}
            </span>
          </div>
          <div className="members-stat-card">
            <span className="members-stat-label">Admins</span>
            <span className="members-stat-value">
              {researchers.filter((r) => formatRole(r.role) === "Admin").length}
            </span>
          </div>
        </div>

        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
        >
          {filteredResearchers.map((researcher) => {
            const status = formatStatus(researcher.status);
            const isPending =
              researcher.isActive === false &&
              researcher.deactivationStatus === false;

            const isDeactivated = researcher.deactivationStatus === true;

            const isActive =
              researcher.isActive === true &&
              researcher.deactivationStatus === false;

            const showReactivate = isDeactivated;
            const isToggling = togglingStatusId === researcher.id;
            const isDeleting = deletingId === researcher.id;

            return (
              <div
                key={researcher.id}
                className={`researcher-card ${isDeactivated ? "researcher-card--deactivated" : ""}`}
                onClick={() => {
                  const isPending =
                    researcher.isActive === false &&
                    researcher.deactivationStatus === false;

                  if (!isPending) {
                    navigate(
                      `/projects?user_id=${researcher.id}&name=${researcher.name}`,
                      {
                        state: { memberName: researcher.name },
                      },
                    );
                  }
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                  >
                    <div
                      className="researcher-avatar"
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        background: profilePics[researcher.id]
                          ? "transparent"
                          : "linear-gradient(135deg, #6d8ac4, #002264)",
                        color: "#fff",
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        border: "2px solid rgba(255, 255, 255, 0.1)",
                        opacity: isDeactivated || isPending ? 0.6 : 1,
                      }}
                    >
                      {profilePics[researcher.id] ? (
                        <img
                          src={profilePics[researcher.id]}
                          alt={researcher.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        researcher.initials
                      )}
                    </div>

                    <div className="researcher-info">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <h3
                          style={{
                            color: isDeactivated ? "#6b7280" : "white",
                            margin: 0,
                            fontSize: "1rem",
                          }}
                        >
                          {researcher.name}
                        </h3>
                      </div>
                      <p
                        style={{
                          color: "#9ca3af",
                          fontSize: "0.875rem",
                          margin: "0.25rem 0",
                        }}
                      >
                        {researcher.email}
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: "0.75rem",
                          fontSize: "0.75rem",
                          alignItems: "center",
                        }}
                      >
                        <span style={{ color: "#cbd5e1" }}>
                          {researcher.project_count} Projects
                        </span>
                        <span
                          style={{
                            color: getRoleColor(researcher.role),
                            fontWeight: 500,
                          }}
                        >
                          {formatRole(researcher.role)}
                        </span>
                        {isPending && (
                          <span
                            style={{
                              fontSize: "0.65rem",
                              fontWeight: 600,
                              color: "#ffffff",
                              background: "rgba(255, 255, 255, 0.12)",
                              border: "1px solid rgba(255, 255, 255, 0.35)",
                              borderRadius: "999px",
                              padding: "2px 8px",
                              letterSpacing: "0.04em",
                              textTransform: "uppercase",
                            }}
                          >
                            Pending
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    className="researcher-actions"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.75rem",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isPending ? (
                      <button
                        title="Remove pending user"
                        onClick={(e) => handleDeleteUser(researcher, e)}
                        disabled={isDeleting}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: isDeleting ? "not-allowed" : "pointer",
                          display: "flex",
                          alignItems: "center",
                          padding: "6px",
                          borderRadius: "6px",
                          color: isDeleting ? "#6b7280" : "#ef4444",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background =
                            "rgba(239,68,68,0.1)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = "transparent")
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    ) : (
                      <>
                        <div
                          className="role-dropdown-wrapper"
                          ref={(el) =>
                            (dropdownRefs.current[researcher.id] = el)
                          }
                        >
                          <button
                            className="role-dropdown-trigger"
                            onClick={() =>
                              setOpenDropdownId(
                                openDropdownId === researcher.id
                                  ? null
                                  : researcher.id,
                              )
                            }
                            disabled={
                              updatingRoleId === researcher.id || isDeactivated
                            }
                          >
                            <span
                              className="role-dot"
                              style={{
                                background: getRoleColor(researcher.role),
                              }}
                            />
                            <span className="role-label">
                              {updatingRoleId === researcher.id
                                ? "Updating..."
                                : formatRole(researcher.role)}
                            </span>
                            <ChevronDown
                              size={14}
                              className={`role-chevron ${openDropdownId === researcher.id ? "open" : ""}`}
                            />
                          </button>

                          {openDropdownId === researcher.id && (
                            <div className="role-dropdown-menu">
                              {ROLES.map((role) => {
                                const isRoleActive =
                                  formatRole(researcher.role) === role;
                                return (
                                  <button
                                    key={role}
                                    className={`role-dropdown-item ${isRoleActive ? "active" : ""}`}
                                    onClick={() =>
                                      handleRoleChange(researcher, role)
                                    }
                                  >
                                    <span
                                      className="role-dot"
                                      style={{
                                        background:
                                          role === "Admin"
                                            ? "#fbbf24"
                                            : role === "Collaborator"
                                              ? "#a78bfa"
                                              : "#10b981",
                                      }}
                                    />
                                    {role}
                                    {isRoleActive && (
                                      <span className="role-active-tick">
                                        ✓
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <button
                          className={`member-status-btn ${showReactivate ? "member-status-btn--reactivate" : "member-status-btn--deactivate"}`}
                          onClick={(e) => handleToggleStatus(researcher, e)}
                          disabled={isToggling}
                        >
                          {isToggling
                            ? "Updating..."
                            : showReactivate
                              ? "Reactivate"
                              : "Deactivate"}
                        </button>
                      </>
                    )}

                    <ChevronRight
                      size={18}
                      style={{ color: "#6b7280", cursor: "pointer" }}
                      onClick={() => {
                        const isPending =
                          researcher.isActive === false &&
                          researcher.deactivationStatus === false;

                        if (!isPending) {
                          navigate(
                            `/projects?user_id=${researcher.id}&name=${researcher.name}`,
                            {
                              state: { memberName: researcher.name },
                            },
                          );
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredResearchers.length === 0 && (
          <p className="members-empty">No members found.</p>
        )}

        <AddMemberModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAddMember={handleAddMember}
        />
      </div>
    </div>
  );
};

export default Researchers;
